import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import type { Category, Item } from '../../lib/supabase'
import type { useSwipeRows } from '../../hooks/useSwipeRows'
import type { useUndoDelete } from '../../hooks/useUndoDelete'
import { basketSegments } from '../../lib/segments'
import { clock } from '../../lib/trips'
import { Hero, SegmentBar } from '../list/Hero'
import { SwipeRow } from '../list/SwipeRow'
import { BasketBar } from '../list/BasketBar'
import { BasketSheet } from '../list/BasketSheet'
import { UndoToast } from '../list/UndoToast'
import { CloseIcon } from '../icons'

// Shopping mode (phone): the list in the store's order (lib/storeRoute.ts),
// the trip's clock, and the basket. Swipe right or tap the ring puts an item
// in the basket; swipe left deletes; tapping a row edits it. The basket bar
// opens the basket, where Finish trip ends the trip (for everyone in it). ✕
// leaves without finishing: the trip and its basket wait until you come back.
// With others shopping too (045), ✕ asks: Leave trip takes you out of it
// (theirs carries on), Just close keeps you in.

export function ShoppingMode({
  ordered, categories, storeName, startedAt, inBasket, total, basket, swipe, undo, undoRef, tintFor,
  onCheck, onEdit, onDelete, onPutBack, onFinish, onChangeStore, onLeave, others = 0, onLeaveTrip,
}: {
  /** The open items, in the store's order. */
  ordered: Item[]
  categories: Category[]
  storeName: string | null
  startedAt: string
  inBasket: number
  total: number
  basket: Item[]
  swipe: ReturnType<typeof useSwipeRows>
  undo: ReturnType<typeof useUndoDelete>
  undoRef: React.Ref<HTMLButtonElement>
  tintFor: (categoryId: string | null) => string
  onCheck: (id: string) => void
  onEdit: (id: string) => void
  onDelete: (id: string, viaKeyboard?: boolean) => void
  onPutBack: (id: string) => void
  onFinish: () => void
  onChangeStore: () => void
  /** ✕: close shopping mode, still in the trip. */
  onLeave: () => void
  /** How many others are shopping in this trip. */
  others?: number
  /** Out of the trip, which carries on for the others. */
  onLeaveTrip?: () => void
}) {
  const [basketOpen, setBasketOpen] = useState(false)
  const [asking, setAsking] = useState(false)
  const close = others > 0 && onLeaveTrip ? () => setAsking(true) : onLeave
  const { close: closeRows } = swipe
  useEffect(() => { if (basketOpen) closeRows() }, [basketOpen, closeRows])

  // Runs of one category in the store's order get a small head, so the list
  // still says which part of the store you're in.
  const runs = useMemo(() => {
    const byId = new Map(categories.map(c => [c.id, c]))
    const out: { key: string; category: Category | null; items: Item[] }[] = []
    for (const item of ordered) {
      const category = item.category_id ? byId.get(item.category_id) ?? null : null
      const last = out[out.length - 1]
      if (last && last.category?.id === category?.id) last.items.push(item)
      else out.push({ key: `${category?.id ?? 'other'}-${out.length}`, category, items: [item] })
    }
    return out
  }, [ordered, categories])

  const left = ordered.length
  return (
    <div className="ml-shopping" role="dialog" aria-modal="true" aria-label="Shopping">
      <header className="ml-shopping__top">
        <button type="button" className="ml-shopping__close" aria-label="Leave shopping mode" onClick={close}>
          <CloseIcon size={22} />
        </button>
        <button type="button" className="ml-shopping__store" onClick={onChangeStore} aria-label={`Store: ${storeName ?? 'Somewhere else'}. Change store`}>
          <span className="ml-label">Shopping at</span>
          <span className="ml-shopping__store-name">{storeName ?? 'Somewhere else'}</span>
        </button>
        <Elapsed since={startedAt} />
      </header>

      <div className="ml-shopping__main" onScroll={closeRows}>
        <Hero count={left} label={left === 0 ? 'Done for today' : left === 1 ? 'Item to get' : 'Items to get'} layout="phone">
          <SegmentBar segments={basketSegments(inBasket, total)} label={`${inBasket} of ${total} in the basket`} />
        </Hero>

        {left === 0
          ? (
            <div className="ml-empty">
              <p className="ml-label ml-empty__title">{inBasket > 0 ? 'Everything is in the basket' : 'Nothing on the list'}</p>
              {inBasket > 0 && (
                <button type="button" className="ml-btn ml-btn--primary ml-btn--wide" onClick={onFinish}>Finish trip</button>
              )}
            </div>
          )
          : runs.map(run => (
            <section key={run.key} className="ml-section" style={{ '--ml-tint': tintFor(run.category?.id ?? null) } as CSSProperties}>
              <h2 className="ml-section__head">
                <span className="ml-dot" aria-hidden="true" />
                <span className="ml-label ml-section__label">{run.category?.name ?? 'Other'}</span>
              </h2>
              <ul className="ml-rows">
                {run.items.map(item => (
                  <SwipeRow key={item.id} item={item} mode="shop"
                    tint={tintFor(item.category_id)}
                    visual={swipe.visual(item.id)}
                    bind={swipe.bind(item.id)}
                    onCheck={onCheck}
                    onEdit={onEdit}
                    onDelete={onDelete} />
                ))}
              </ul>
            </section>
          ))}
      </div>

      <div className="ml-shopping__dock">
        <UndoToast ref={undoRef} pending={undo.pending} layout="phone" onUndo={undo.undo} />
        <BasketBar inBasket={inBasket} total={total} last={basket[0]?.name ?? null} onOpen={() => setBasketOpen(true)} />
      </div>

      <BasketSheet
        open={basketOpen}
        basket={basket}
        total={total}
        tintFor={tintFor}
        zIndex={30}
        onPutBack={onPutBack}
        onFinish={() => { setBasketOpen(false); onFinish() }}
        onClose={() => setBasketOpen(false)}
      />

      {asking && (
        <div className="ml-shopping__ask" role="alertdialog" aria-modal="true" aria-labelledby="ml-leave-title"
          aria-describedby="ml-leave-text">
          <h2 id="ml-leave-title" className="ml-display ml-sheet-title">Leave the trip?</h2>
          <p id="ml-leave-text" className="ml-caption">
            {others === 1 ? 'Someone else is' : `${others} others are`} still shopping. Leave and the trip carries on for them.
          </p>
          <button type="button" className="ml-btn ml-btn--primary ml-btn--wide"
            onClick={() => { setAsking(false); onLeave() }}>Just close</button>
          <button type="button" className="ml-btn ml-btn--secondary ml-btn--wide"
            onClick={() => { setAsking(false); onLeaveTrip?.() }}>Leave trip</button>
        </div>
      )}
    </div>
  )
}

/** The trip's stopwatch, from the moment shopping started. */
function Elapsed({ since }: { since: string }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  const ms = Math.max(0, now - Date.parse(since))
  return (
    <span className="ml-shopping__clock ml-num" role="timer" aria-label={`Shopping for ${Math.floor(ms / 60_000)} minutes`}>
      {clock(ms)}
    </span>
  )
}
